import { useState } from 'react';
import {
  InputLabel,
  Button,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Box,
  InputAdornment,
} from '@mui/material';
import { jidsConsumableSupplyNotification } from '../../../../../data/forms/nrc/consumableSupplyNotificationDropdownOptions.js';
import RetailerLookupButton from '../retailerLookupButton.js';
import axios from 'axios';

const ConsumableSupplyNotificationForm = ({
  translations,
  user,
  handleFormSelectionReset,
  setSnackbarMessage,
  setIsSnackbarOpen,
  setSnackbarSeverity,
}) => {
  const [form, setForm] = useState({
    jurisdiction: '',
    retailerId: '',
    retailerName: '',
    address: '',
    phoneNumber: '',
    callerName: '',
    supplyChangeComments: '',
    email: user.email,
    emailAddress: user.email,
    displayName: user.displayName,
    userId: user.userId,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = e => {
    const { name, value } = e.target;

    setForm({
      ...form,
      [name]: value,
    });
  };

  const validatePhoneNumber = phoneNumber => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phoneNumber);
  };

  const handleClear = () => {
    setForm({
      ...form,
      jurisdiction: '',
      retailerId: '',
      retailerName: '',
      address: '',
      phoneNumber: '',
      callerName: '',
      supplyChangeComments: '',
    });
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await axios({
        method: 'POST',
        url: process.env.REACT_APP_ENDPOINT_CONSUMABLE_SUPPLY_NOTIFICATION_FORM,
        data: {
          jid: form.jurisdiction,
          retailerId: form.retailerId,
          phone: `+1${form.phoneNumber}`,
          formType: 'uscc',
        },
      });
      setSnackbarMessage('Message Delivered Successfully!');
      setSnackbarSeverity('success');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
      handleClear();
      handleFormSelectionReset();
    } catch (error) {
      console.error('Error sending text message:', error);
      setSnackbarMessage(
        'There was an error sending the text message. Please try again. If the problem persists, contact support.',
      );
      setSnackbarSeverity('error');
      setIsSnackbarOpen(true);
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    const {
      jurisdiction,
      retailerId,
      retailerName,
      address,
      phoneNumber,
      callerName,
    } = form;

    if (
      !jurisdiction ||
      !retailerId ||
      !retailerName ||
      !address ||
      !phoneNumber ||
      !callerName
    ) {
      return false;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      return false;
    }

    return true;
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{
          display: 'flex',
          justifyItems: 'center',
          width: { xs: 'auto', md: 'var(--forms-layout-width, 90vw)' },
        }}
      >
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl
            required
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          >
            <InputLabel>
              {translations.jurisdiction || 'Jurisdiction'}
            </InputLabel>
            <Select
              value={form.jurisdiction}
              label={translations.jurisdiction || 'Jurisdiction'}
              onChange={handleChange}
              name='jurisdiction'
            >
              {jidsConsumableSupplyNotification.map(jid => (
                <MenuItem value={jid}>{jid}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label='Retailer Name'
            name='retailerName'
            required
            value={form.retailerName}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
            value={form.phoneNumber}
            onChange={handleChange}
            label='Phone Number'
            name='phoneNumber'
            required
            error={
              form.phoneNumber !== '' && !validatePhoneNumber(form.phoneNumber)
            }
          />
          <TextField
            label='Associate Name'
            disabled
            value={`${user.firstName} ${user.lastName}`}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Tooltip
            title={'Enter a Retailer ID (numeric values only) to lookup'}
            arrow
          >
            <TextField
              label='Retailer ID'
              required
              name='retailerId'
              fullWidth
              value={form.retailerId}
              onChange={handleChange}
              sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
              type='number'
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <RetailerLookupButton form={form} setForm={setForm} />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Tooltip>
          <TextField
            label='Address'
            name='address'
            required
            value={form.address}
            onChange={handleChange}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
          <TextField
            required
            label='Caller Name'
            name='callerName'
            fullWidth
            value={form.newPhone}
            onChange={handleChange}
            sx={{ backgroundColor: '#ffffff', marginBottom: '15px' }}
          />
          <TextField
            label='Supply Change Comments'
            name='supplyChangeComments'
            value={form.supplyChangeComments}
            onChange={handleChange}
            multiline
            rows={4.1}
            sx={{
              backgroundColor: '#ffffff',
              marginBottom: '15px',
              width: '100%',
            }}
          />
        </Grid>
        <Grid
          size={12}
          sx={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant='contained'
            sx={{
              backgroundColor: 'var(--aldebaran-yellow)',
              color: '#000000',
              fontFamily: 'BrightstarSans-Regular',
              width: '10%',
            }}
            onClick={handleClear}
          >
            Clear
          </Button>
          <Tooltip
            title={
              !isFormValid()
                ? 'Please fill in all required fields to enable the Submit button'
                : ''
            }
            arrow
          >
            <Box>
              <Button
                disabled={!isFormValid() || isSubmitting}
                variant='contained'
                sx={{
                  backgroundColor: 'var(--vega-blue)',
                  fontFamily: 'BrightstarSans-Regular',
                  width: '100%',
                }}
                onClick={handleSubmit}
                loading={isSubmitting}
              >
                {'Submit'}
              </Button>
            </Box>
          </Tooltip>
        </Grid>
      </Grid>
    </>
  );
};

export default ConsumableSupplyNotificationForm;
